/**
 * verdictPrint — generiert eine druckfertige HTML-Seite aus einem TrialResult
 * und öffnet sie in einem neuen Tab mit automatischem print-Dialog.
 *
 * Sprint I/4 (2026-04-29): Anstelle einer Server-PDF-Pipeline (würde
 * Headless-Chrome auf Cloudflare Workers brauchen — gibt es schlicht
 * nicht für Workers) nutzen wir den Browser-Print-Dialog. Jeder moderne
 * Desktop-Browser bietet "Speichern als PDF" als virtuellen Druckertarget
 * an — das ist 1) zero-Bundle-Kosten, 2) bessere Schriftqualität als
 * jspdf+html2canvas, 3) druckt auch direkt auf Papier.
 *
 * Trade-off: User klickt einmal mehr (Print-Dialog → Speichern als PDF).
 * Den Trade nehmen wir; der Vorteil von Cross-Browser-Konsistenz und keiner
 * weiteren Abhängigkeit überwiegt deutlich.
 *
 * Sicherheit: Wir bauen das HTML strikt mit escapeHtml(), keine User-Eingabe
 * landet ungeprüft in Markup. So sind Prompt-Injection-Reste oder bösartige
 * UTF-Sequenzen aus dem caseContext ungefährlich.
 */

import type { TrialResult } from "./api";

/**
 * Eskapiert HTML-Sonderzeichen. Pflicht für JEDEN String aus User-Input
 * oder LLM-Output — sonst landet ein \<script\> aus dem caseContext im
 * neuen Tab und kann Schaden anrichten (gleicher Origin wie die App!).
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function pct(n: number): string {
  return `${Math.round(Math.max(0, Math.min(100, n)))}%`;
}

function fmtEur(n: number): string {
  return n.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Baut die "Timeline" — chronologische Schritte vom Mock-Jury-Verdict bis
 * zur möglichen Klage. Geht Hand in Hand mit den nextSteps des Advocates,
 * fügt aber das Verdict-Datum + (falls passend) Frist + Eskalations-Punkte
 * hinzu, damit der User sieht WANN was passieren sollte.
 */
function buildTimelineRows(result: TrialResult): Array<{
  when: string;
  what: string;
  detail?: string;
}> {
  const today = new Date(result.createdAt);
  const fmt = (d: Date) => d.toLocaleDateString("de-DE", { year: "numeric", month: "2-digit", day: "2-digit" });
  const addDays = (n: number): Date => new Date(today.getTime() + n * 86400_000);

  const rec = result.advocateOpinion?.recommendation ?? "settle";
  const rows: Array<{ when: string; what: string; detail?: string }> = [
    {
      when: fmt(today),
      what: "Mock-Jury-Verdict erhalten",
      detail: `${result.swarmVerdicts.length} Juroren · ${result.winProbability}% Erfolg · Vergleichsempfehlung ${fmtEur(result.settlementRecommendation)} EUR`,
    },
  ];

  if (rec === "pursue") {
    rows.push(
      { when: fmt(addDays(2)), what: "Beweismaterial sichern", detail: "Fotos, Korrespondenz, Zeugenliste finalisieren" },
      { when: fmt(addDays(5)), what: "Förmliche Aufforderung mit 14-Tage-Frist verschicken", detail: "Per Einschreiben mit Rückschein" },
      { when: fmt(addDays(19)), what: "Frist läuft ab — falls keine Reaktion: Klagevorbereitung", detail: "Falls verbunden mit RSV: Deckungsanfrage stellen" },
      { when: fmt(addDays(33)), what: "Klage einreichen / Mahnbescheid beantragen", detail: "Mahnbescheid bei Geldforderungen, Klage bei Streit über Anspruch" }
    );
  } else if (rec === "settle") {
    rows.push(
      { when: fmt(addDays(1)), what: "Vergleichsangebot in Höhe der Empfehlung schriftlich unterbreiten", detail: `Ziel: ~${fmtEur(result.settlementRecommendation)} EUR` },
      { when: fmt(addDays(11)), what: "Frist (10 Werktage) zur Annahme abgelaufen", detail: "Bei Schweigen: Eskalation Richtung Klage prüfen" },
      { when: fmt(addDays(25)), what: "Falls keine Einigung: Mock-Jury-Verdict mit Anwalt durchsprechen", detail: "Erstberatung ~190 EUR netto" }
    );
  } else {
    rows.push(
      { when: fmt(addDays(1)), what: "Fall schriftlich abschließen, Unterlagen archivieren" },
      { when: fmt(addDays(7)), what: "Falls Gegenseite überraschend zahlen will: ohne Anerkenntnis annehmen" }
    );
  }

  return rows;
}

function buildHTML(result: TrialResult, caseContext: string): string {
  const op = result.advocateOpinion;
  const timeline = buildTimelineRows(result);
  const today = new Date(result.createdAt).toLocaleDateString("de-DE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const stats = (() => {
    let fav = 0, neu = 0, unf = 0;
    for (const v of result.swarmVerdicts) {
      if (v.verdict === "favorable") fav++;
      else if (v.verdict === "neutral") neu++;
      else unf++;
    }
    const n = result.swarmVerdicts.length || 1;
    return {
      fav, neu, unf, n,
      favPct: pct((fav / n) * 100),
      neuPct: pct((neu / n) * 100),
      unfPct: pct((unf / n) * 100),
    };
  })();

  const recLabel =
    op?.recommendation === "pursue"
      ? "Klagen / Durchsetzen"
      : op?.recommendation === "settle"
        ? "Vergleich anstreben"
        : op?.recommendation === "drop"
          ? "Fall fallenlassen"
          : "—";

  const top5 = [...result.swarmVerdicts]
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 5);

  const precRows = result.precedentsUsed
    .slice(0, 6)
    .map(
      (p) => `
        <tr>
          <td class="px font-mono">${escapeHtml(p.citation)}</td>
          <td class="px text-right">${pct(p.relevance * 100)}</td>
          <td class="px small">${escapeHtml((p.snippet || "").slice(0, 240))}</td>
        </tr>`
    )
    .join("");

  const verdictRows = top5
    .map(
      (v) => `
        <tr class="${v.verdict}">
          <td class="px small">${escapeHtml(v.name ?? v.model)}</td>
          <td class="px small">${escapeHtml(v.title ?? v.role ?? "")}</td>
          <td class="px text-right">${pct(v.confidence * 100)}</td>
          <td class="px small">${escapeHtml((v.reasoning || "").slice(0, 360))}</td>
        </tr>`
    )
    .join("");

  const timelineRows = timeline
    .map(
      (t) => `
        <tr>
          <td class="px font-mono small text-nowrap">${escapeHtml(t.when)}</td>
          <td class="px"><strong>${escapeHtml(t.what)}</strong>${t.detail ? `<br/><span class="muted">${escapeHtml(t.detail)}</span>` : ""}</td>
        </tr>`
    )
    .join("");

  const nextStepsList = (op?.nextSteps ?? [])
    .map((s) => `<li>${escapeHtml(s)}</li>`)
    .join("");

  const suggestedLetter = op?.suggestedLetter
    ? `
      <section class="suggestion">
        <h3>Brief-Vorschlag</h3>
        <p><strong>${escapeHtml(op.suggestedLetter.headline)}</strong>${op.suggestedLetter.recipient ? ` · Empfänger: ${escapeHtml(op.suggestedLetter.recipient)}` : ""}</p>
        <p class="muted small">${escapeHtml(op.suggestedLetter.reason)}</p>
        <p class="small muted">Typ: <code>${escapeHtml(op.suggestedLetter.type)}</code> — direkt im App unter „Brief entwerfen" generierbar.</p>
      </section>`
    : "";

  return `<!doctype html>
<html lang="de">
<head>
<meta charset="utf-8" />
<title>Shadow Court — Verdict ${escapeHtml(result.caseId)}</title>
<style>
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #111; font: 13px/1.45 -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif; }
  .page { padding: 28px 36px; max-width: 800px; margin: 0 auto; }
  .header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #1f2937; padding-bottom: 12px; margin-bottom: 18px; }
  .header h1 { margin: 0; font-size: 22px; letter-spacing: -0.01em; }
  .header .meta { text-align: right; font-size: 11px; color: #555; }
  .badge { display: inline-block; padding: 3px 8px; border-radius: 9999px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; border: 1px solid currentColor; }
  .badge-pursue { color: #047857; background: #d1fae5; }
  .badge-settle { color: #92400e; background: #fef3c7; }
  .badge-drop { color: #991b1b; background: #fee2e2; }
  .hero { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; padding: 16px 20px; margin-bottom: 18px; }
  .hero .hd { font-size: 16px; font-weight: 600; }
  .hero .meta-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 12px; }
  .stat { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; }
  .stat .lbl { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
  .stat .val { font-size: 18px; font-weight: 700; margin-top: 2px; font-family: ui-monospace, SFMono-Regular, monospace; }
  h2 { margin: 22px 0 8px; font-size: 14px; text-transform: uppercase; letter-spacing: 0.06em; color: #374151; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
  h3 { margin: 14px 0 6px; font-size: 13px; }
  p { margin: 6px 0; }
  ol, ul { margin: 6px 0 6px 20px; padding: 0; }
  li { margin-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; color: #4b5563; text-align: left; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; }
  td { padding: 6px 8px; vertical-align: top; border-bottom: 1px solid #f3f4f6; font-size: 12px; }
  td.text-right, th.text-right { text-align: right; }
  td.font-mono { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 11px; }
  td.small, .small { font-size: 11px; }
  .text-nowrap { white-space: nowrap; }
  .muted { color: #6b7280; }
  .px { padding: 6px 8px; }
  .bar { height: 10px; border-radius: 9999px; overflow: hidden; background: #e5e7eb; display: flex; margin: 6px 0; }
  .bar > div { height: 100%; }
  .bar-fav { background: #10b981; }
  .bar-neu { background: #f59e0b; }
  .bar-unf { background: #ef4444; }
  .legend { font-size: 11px; color: #4b5563; display: flex; justify-content: space-between; }
  tr.favorable td { background: #f0fdf4; }
  tr.neutral td { background: #fffbeb; }
  tr.unfavorable td { background: #fef2f2; }
  .suggestion { background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 8px; padding: 10px 14px; margin-top: 12px; }
  .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #6b7280; line-height: 1.4; }
  .toolbar { position: sticky; top: 0; background: #fffbe6; border-bottom: 1px solid #fcd34d; padding: 8px 12px; display: flex; gap: 8px; align-items: center; justify-content: space-between; font-size: 12px; z-index: 5; }
  .toolbar button { background: #1f2937; color: white; border: none; border-radius: 6px; padding: 6px 14px; font-size: 12px; cursor: pointer; }
  .toolbar button:hover { background: #111827; }
  @media print {
    .toolbar { display: none; }
    .page { padding: 0 12mm; }
    body { font-size: 11.5px; }
    h2 { page-break-after: avoid; }
    table, tr, td, th { page-break-inside: avoid; }
    section, .hero { page-break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="toolbar">
    <span>Tipp: Im Druck-Dialog „Speichern als PDF" auswählen.</span>
    <button onclick="window.print()">📄 Drucken / als PDF speichern</button>
  </div>
  <div class="page">
    <div class="header">
      <div>
        <h1>Shadow Court — Mock-Jury Verdict</h1>
        <div class="small muted">Case <span class="font-mono">${escapeHtml(result.caseId)}</span>${result.jurisdiction ? ` · Jurisdiction <strong>${escapeHtml(result.jurisdiction)}</strong>` : ""}</div>
      </div>
      <div class="meta">
        ${today}<br/>
        ${result.swarmVerdicts.length} Juroren · ${result.precedentsUsed.length} Präzedenzfälle
      </div>
    </div>

    ${
      op
        ? `
    <section class="hero">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;">
        <div>
          <div class="small muted">Empfehlung deines Anwalts</div>
          <div class="hd">${escapeHtml(op.lawyerName)} · ${escapeHtml(op.lawyerTitle)}</div>
          <div class="small muted">${escapeHtml(op.lawyerAffiliation)} (fiktiver Advisor)</div>
        </div>
        <span class="badge badge-${op.recommendation}">${escapeHtml(recLabel)}</span>
      </div>
      <p style="font-size: 15px; font-weight: 600; margin-top: 12px;">„${escapeHtml(op.headline)}"</p>
      <div class="meta-grid">
        <div class="stat"><div class="lbl">Erwartungswert</div><div class="val">${fmtEur(op.expectedValueEur)} EUR</div></div>
        <div class="stat"><div class="lbl">Echten Anwalt?</div><div class="val">${op.lohntAnwalt ? "Ja" : "Nein"}</div></div>
        <div class="stat"><div class="lbl">Zuverlässigkeit</div><div class="val">${escapeHtml(op.uncertainty)}</div></div>
      </div>
    </section>

    <h2>Einschätzung</h2>
    <p>${escapeHtml(op.reasoning)}</p>

    ${nextStepsList ? `<h2>Nächste Schritte</h2><ol>${nextStepsList}</ol>` : ""}
    ${suggestedLetter}
    `
        : ""
    }

    <h2>Mock-Jury Statistik</h2>
    <p class="small muted">${stats.n} valide Stimmen · Verteilung der Juror-Voten</p>
    <div class="bar">
      <div class="bar-fav" style="width:${stats.favPct}"></div>
      <div class="bar-neu" style="width:${stats.neuPct}"></div>
      <div class="bar-unf" style="width:${stats.unfPct}"></div>
    </div>
    <div class="legend">
      <span>● pro Mandant: ${stats.fav} (${stats.favPct})</span>
      <span>● neutral: ${stats.neu} (${stats.neuPct})</span>
      <span>● contra: ${stats.unf} (${stats.unfPct})</span>
    </div>

    <h3>Top-5 Juror-Stimmen (Confidence-gewichtet)</h3>
    <table>
      <thead>
        <tr><th>Juror</th><th>Funktion</th><th class="text-right">Conf.</th><th>Begründung</th></tr>
      </thead>
      <tbody>${verdictRows || `<tr><td colspan="4" class="muted">(keine Stimmen)</td></tr>`}</tbody>
    </table>

    <h2>Strategie (Battle-Plan)</h2>
    <p>${escapeHtml(result.battlePlan)}</p>

    <h2>Präzedenzfälle</h2>
    <table>
      <thead>
        <tr><th>Citation</th><th class="text-right">Sim.</th><th>Snippet</th></tr>
      </thead>
      <tbody>${precRows || `<tr><td colspan="3" class="muted">Keine Präzedenzfälle gefunden.</td></tr>`}</tbody>
    </table>
    ${precRows ? `<p class="muted small">Urteilsdaten teilweise via Open Legal Data e.V. (de.openlegaldata.io), Lizenz ODbL v1.0.</p>` : ""}

    <h2>Timeline / Eskalationspfad</h2>
    <table>
      <thead>
        <tr><th>Datum</th><th>Schritt</th></tr>
      </thead>
      <tbody>${timelineRows}</tbody>
    </table>

    <h2>Sachverhalt</h2>
    <p class="small">${escapeHtml(caseContext.slice(0, 2000))}${caseContext.length > 2000 ? " […]" : ""}</p>

    <div class="footer">
      Hinweis nach § 2 RDG: Shadow Court ist keine Anwaltskanzlei. Diese
      Auswertung ist eine automatisierte Mock-Jury-Simulation auf Basis von
      LLM-Personas — kein verbindliches Rechtsgutachten und kein
      Mandatsverhältnis. Für rechtsverbindliche Schritte konsultiere bitte
      einen zugelassenen Rechtsanwalt. · Erstellt mit Shadow Court · ${today}
    </div>
  </div>
</body>
</html>`;
}

/**
 * Öffnet einen neuen Tab mit dem druckbaren HTML, wartet bis es geladen ist
 * und ruft dann window.print() auf. Der User wählt im Browser-Dialog
 * "Speichern als PDF" — alle Desktop-Browser bieten das nativ an.
 *
 * Failure-Modi:
 *   - Pop-up-Blocker → window.open() liefert null → wir alerten und brechen ab.
 *   - Mobile Safari → öffnet zwar Tab, aber print() wird ignoriert. Die
 *     Toolbar im HTML bietet einen sichtbaren Print-Button als Fallback.
 */
export function printVerdict(result: TrialResult, caseContext: string): void {
  const html = buildHTML(result, caseContext);
  const win = window.open("", "_blank", "noopener,noreferrer,width=900,height=1200");
  if (!win) {
    // eslint-disable-next-line no-alert
    alert(
      "Verdict-Druck konnte nicht geöffnet werden — bitte Pop-ups für diese Seite erlauben und erneut versuchen."
    );
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  // Print-Dialog erst nach onload, damit Webfonts/Tabellen vollständig
  // gemessen sind. Fallback per setTimeout für Browser ohne onload-Event.
  const triggerPrint = () => {
    try {
      win.focus();
      win.print();
    } catch {
      /* ignore — Toolbar-Button bleibt verfügbar */
    }
  };
  if (win.document.readyState === "complete") {
    setTimeout(triggerPrint, 80);
  } else {
    win.addEventListener("load", () => setTimeout(triggerPrint, 80));
  }
}
